import VueRouter, { NavigationGuard, Route, RouteConfig } from 'vue-router';
import { DataBank } from '@/helpers/data_bank';
import { Action } from '@/helpers/action';
import { FeatureFlags } from '@/constants/feature_flags';
import { CheckFeatureFlag } from '@/data/feature_flag_check';
import volieStore from '../store';
import * as Role from '../model/roles';

// Pages that now live in the React app. The routes stay so existing links and
// bookmarks land on the new page instead of the retired Vue one.
const redirectToPulseV2 = (
    buildPath: (to: Route) => string,
    flagKey?: string,
): NavigationGuard => (to, from, next) => {
    const redirect = () => {
        window.location.replace(import.meta.env.VITE_APP_PULSE_V2_BASE_URL + buildPath(to));
        next(false);
    };

    if (!flagKey) {
        redirect();
        return;
    }

    CheckFeatureFlag(flagKey)
        .then((enabled) => {
            if (enabled) {
                redirect();
                return;
            }
            next();
        })
        .catch(() => next());
};

// Routes
const routes: Array<RouteConfig> = [

    // Access Groups
    {
        path: "/access_groups",
        name: "access_group_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/access_groups/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Access Groups",
            recordVisits: true,
        },
    },
    {
        path: "/access_groups/new",
        name: "access_group_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/access_groups/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Access Group"
        },
    },
    {
        path: "/access_groups/:vid/edit",
        name: "access_group_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/access_groups/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Access Group"
        },
    },

    // Access Groups - Account Accesses
    {
        path: "/access_groups/:access_group_vid/account_accesses",
        name: "account_access_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_accesses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Account Accesses",
            recordVisits: true,
        },
    },
    {
        path: "/access_groups/:access_group_vid/account_accesses/new",
        name: "account_access_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_accesses/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Account Access"
        },
    },
    {
        path: "/access_groups/:access_group_vid/account_accesses/:vid/edit",
        name: "account_access_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_accesses/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Account Access"
        },
    },

    // Access Groups - Organization Accesses
    {
        path: "/access_groups/:access_group_vid/account_accesses/:account_access_vid/organization_accesses/new",
        name: "organization_access_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_accesses/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Organization Access"
        },
    },
    {
        path: "/access_groups/:access_group_vid/account_accesses/:account_access_vid/organization_accesses/:vid/edit",
        name: "organization_access_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_accesses/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Organization Access"
        },
    },

    // Access Groups - Campaign Accesses
    {
        path: "/access_groups/:access_group_vid/account_accesses/:account_access_vid/organization_accesses/:organization_access_vid/campaign_accesses/new",
        name: "campaign_access_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_accesses/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Campaign Access"
        },
    },
    {
        path: "/access_groups/:access_group_vid/account_accesses/:account_access_vid/organization_accesses/:organization_access_vid/campaign_accesses/:vid/edit",
        name: "campaign_access_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_accesses/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Campaign Access"
        },
    },

    // Account Plugins
    {
        path: "/account_plugins",
        name: "account_plugin_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_plugins/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Account Plugins",
            recordVisits: true,
        },
    },
    {
        path: "/account_plugins/:vid/show",
        name: "account_plugin_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_plugins/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Account Plugin"
        },
    },
    {
        path: "/account_plugins/new",
        name: "account_plugin_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_plugins/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Account Plugin"
        },
    },
    {
        path: "/account_plugins/:vid/edit",
        name: "account_plugin_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_plugins/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Account Plugin"
        },
    },

    // Accounts
    {
        path: "/super_admin/accounts",
        name: "accounts_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/accounts/super_admin_index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Super Admin Accounts",
            recordVisits: true,
        },
    },
    {
        path: "/account_switcher",
        name: "account_switcher_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/account_switcher/index.vue'),
        meta: {
            allowedRoles: Role.All,
            title: "Account Switcher",
            recordVisits: true,
        },
    },
    {
        path: "/accounts/:vid",
        name: "accounts_edit",
        component: () =>
            import(
                /* webpackChunkName: "group-app" */ "../views/accounts/edit.vue"
            ),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Account"
        },
    },

    // Adf Email
    {
        path: "/adf_emails",
        name: "adf_email_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/adf_emails/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Adf Emails",
            recordVisits: true,
        },
    },
    {
        path: "/adf_emails/:adf_email_key",
        name: "adf_email_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/adf_emails/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Show Adf Email"
        },
    },

    // Adf Email Addresses
    {
        path: "/adf_email_addresses",
        name: "adf_email_addresses_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/adf_email_addresses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Adf Email Addresses",
            recordVisits: true,
        },
    },
    {
        path: '/adf_email_addresses/:vid/edit',
        name: "adf_email_addresses_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/adf_email_addresses/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Edit Adf Email Address"
        },
    },
    {
        path: '/adf_email_addresses/new',
        name: "adf_email_addresses_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/adf_email_addresses/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "New Adf Email Address"
        },
    },

    // Admin
    {
        path: "/admins",
        name: "admins_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/admins/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Admin",
            recordVisits: true,
        },
    },
    {
        path: "/alert_providers/teams",
        name: "teams_alert_provider",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/alert_providers/teams.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Teams Alert Provider",
            recordVisits: true,
        },
    },
    // Agent Statuses
    {
        path: "/agent_statuses",
        name: "agent_statuses_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/agent_statuses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Agent Statuses",
            recordVisits: true,
        },
    },
    {
        path: "/agent_statuses/new",
        name: "agent_statuses_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/agent_statuses/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Agent Status"
        },
    },
    {
        path: "/agent_statuses/:agent_status_key/edit",
        name: "agent_status_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/agent_statuses/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Agent Status"
        },
    },

    //Agent Visibility

    {
        path: "/agent_visibility",
        name: "agent_visibility_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/agent_visibility/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER ],
            title: "Agent Visibility",
            recordVisits: true,
        },
    },

    {
        path: "/agent_visibility/:agent_vid",
        name: "agent_visibility_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/agent_visibility/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER ],
            title: "Show Agent Visibility"
        },
    },

    // Agent Status Rules
    {
        path: "/agent_status_rules",
        name: "agent_status_rule_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/agent_status_rules/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Agent Status Rules",
            recordVisits: true,
        },
    },

    // Agent Status Rules Report
    {
        path: "/agent_status_rules_report",
        name: "agent_status_rules_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/agent_status_rules_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Agent Status Rules Report",
            recordVisits: true,
        },
    },

    // AI Assistants
    {
        path: "/ai_assistants",
        name: "ai_assistants_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/ai_assistants/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "AI Assistants",
            recordVisits: true,
        },
    },
    {
        path: "/ai_assistants/:vid",
        name: "ai_assistants_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/ai_assistants/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show AI Assistant",
        },
    },

    {
        path: "/admin/regions",
        name: "admin_regions_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/regions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Regions",
            recordVisits: true,
        },
    },

    //Alerts
    {
        path: "/alerts",
        name: "alerts_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/alerts/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Alerts",
            recordVisits: true,
        },
    },
    {
        path: "/alerts/:alert_vid",
        name: "alerts_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/alerts/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
        },
    },

    // Alert Types
    {
        path: "/alert_types",
        name: "alert_types_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/alert_types/index.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/alert-types", FeatureFlags.AlertTypesReactAdmin),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Alert Types",
            recordVisits: true,
        },
    },
    {
        path: "/alert_types/new",
        name: "alert_types_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/alert_types/new.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/alert-types", FeatureFlags.AlertTypesReactAdmin),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Alert Type"
        },
    },
    {
        path: "/alert_types/:vid/edit",
        name: "alert_types_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/alert_types/edit.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/alert-types", FeatureFlags.AlertTypesReactAdmin),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Alert Type"
        },
    },

    // Appointment Scheduling Hours
    {
        path: "/appointment_scheduling_hours",
        name: "appointment_scheduling_hours_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/appointment_scheduling_hours/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Appointment Scheduling Hours",
            recordVisits: true,
        },
    },
    {
        path: "/appointment_scheduling_hours/new",
        name: "appointment_scheduling_hours_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/appointment_scheduling_hours/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Appointment Scheduling Hours"
        },
    },

    // Appointment types
    {
        path: "/appointment_types",
        name: "appointment_type_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/appointment_types/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Appointment Types",
            recordVisits: true,
        },
    },
    {
        path: "/appointment_types/new",
        name: "appointment_type_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/appointment_types/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Appointment Type"
        },
    },
    {
        path: "/appointment_types/:appointment_type_key/edit",
        name: "appointment_type_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/appointment_types/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Appointment Type"
        },
    },
    // Automate Deals
    {
        path: "/automate_deals",
        name: "automate_deal_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_deals/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Automate Deals",
            recordVisits: true,
        },
    },
    {
        path: "/automate_deals/:automate_deal_id",
        name: "automate_deal_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_deals/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Automate Deal"
        },
    },

    // Automate Files
    {
        path: "/automate_files",
        name: "automate_file_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_files/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Automate Files",
            recordVisits: true,
        },
    },
    {
        path: "/automate_files/:file_id",
        name: "automate_file_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_files/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Automate File"
        },
    },

    // Automate Repair Order Headers
    {
        path: "/automate_repair_order_headers",
        name: "automate_repair_order_header_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_repair_order_headers/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Automate Repair Order Headers",
            recordVisits: true,
        },
    },
    {
        path: "/automate_repair_order_headers/:repaire_order_header_id",
        name: "automate_repair_order_header_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_repair_order_headers/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Automate Repair Order Header"
        },
    },

    // Automate Repair Order Lines
    {
        path: "/automate_repair_order_lines",
        name: "automate_repair_order_line_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_repair_order_lines/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Automate Repair Order Lines",
            recordVisits: true,
        },
    },
    {
        path: "/automate_repair_order_lines/:repaire_order_line_id",
        name: "automate_repair_order_line_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_repair_order_lines/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Automate Repair Order Line"
        },
    },

    // Automate Sale persons
    {
        path: "/automate_sales_persons",
        name: "automate_sales_person_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_sales_persons/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Automate Sales Persons",
            recordVisits: true,
        },
    },
    {
        path: "/automate_sales_persons/:sales_person_id",
        name: "automate_sales_person_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/automate_sales_persons/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Automate Sales Person"
        },
    },

    // OEM Loyalty Segments
    {
        path: "/utilities/oem_segments",
        name: "oem_segments_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/oem_segments/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
        },
    },
    {
        path: "/oem_segments",
        redirect: "/utilities/oem_segments",
    },

    // Calls
    {
        path: "/calls",
        name: "call_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/calls/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Calls",
            recordVisits: true,
        },
    },

    // Call Transcription Rules

    // {
    //     path: "/call_transcription_rules",
    //     name: "call_transcription_rules_index",
    //     component: () => import( /* webpackChunkName: "group-app" */ '../views/call_transcription_rules/index.vue'),
    //     meta: {
    //         allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
    //     }

    // },
    // {
    //     path: "/call_transcription_rules/new",
    //     name: "call_transcription_rules_new",
    //     component: () => import( /* webpackChunkName: "group-app" */ '../views/call_transcription_rules/new.vue'),
    //     meta: {
    //         allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
    //     }

    // },
    // {
    //     path: "/call_transcription_rules/:vid/edit",
    //     name: "call_transcription_rules_edit",
    //     component: () => import( /* webpackChunkName: "group-app" */ '../views/call_transcription_rules/edit.vue'),
    //     meta: {
    //         allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
    //     },
    // },

    // Call Transcription Rules
    {
        path: "/call_transcriptions",
        name: "call_transcriptions_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/call_transcriptions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Call Transcriptions",
            recordVisits: true,
        }

    },
    {
        path: "/call_transcriptions/post_call/:call_key",
        name: "call_transcriptions_post_call_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/call_transcriptions/post_call_detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Call Transcription"
        },
    },

    // Campaign
    {
        path: "/campaigns",
        name: "campaigns_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaigns/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Campaigns",
            recordVisits: true,
        },
    },
    {
        path: "/campaigns/:vid/edit",
        name: "campaigns_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaigns/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Edit Campaign"
        },
    },
    {
        path: "/campaigns/new",
        name: "campaigns_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaigns/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "New Campaign"
        },
    },
    {
        path: "/campaigns/catalog",
        name: "campaigns_catalog",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaigns/catalog.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Campaign Catalog",
            recordVisits: true,
        },
    },

    // Campaign Attempt Management
    {
        path: "/campaign_attempt_management",
        name: "campaign_attempt_management",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaigns/campaign_attempt_management.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER ],
            hideMainMenu: true,
            title: "Campaign Attempt Management",
            recordVisits: true,
        },
    },

    // Campaign Communication Transaction
    {
        path: "/campaign_communication_transactions",
        name: "campaign_communication_transaction_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_communication_transactions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Campaign Communication Transactions",
            recordVisits: true,
        },
    },

    // Campaign Deployment
    {
        path: "/campaign_deployments",
        name: "campaign_deployments",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_deployments/main.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Campaign Deployments",
            recordVisits: true,
        },
    },

    // Campaign edit
    {
        path: "/campaigns/:campaign_key/edit",
        name: "edit_campaign",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaigns/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Campaign"
        },
    },

    // Campaign mappings
    {
        path: "/campaign_mappings",
        name: "campaign_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_mappings/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Campaign Mappings",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_mappings/new",
        name: "campaign_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_mappings/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Campaign Mapping"
        },
    },
    {
        path: "/campaign_mappings/:mapping_id/edit",
        name: "campaign_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_mappings/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Campaign Mapping"
        },
    },

    // Campaign Types
    {
        path: "/campaign_types",
        name: "campaign_types_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_types/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Campaign Types",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_types/new",
        name: "campaign_types_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_types/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Campaign Type"
        },
    },
    {
        path: "/campaign_types/:vid",
        name: "campaign_types_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/campaign_types/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Campaign Type"
        },
    },

    // Cdk Customer Imports
    {
        path: "/cdk_customer_imports",
        name: "cdk_customer_import_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_customer_imports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CDK Customer Imports",
            recordVisits: true,
        },
    },
    {
        path: "/cdk_customer_imports/:import_id",
        name: "cdk_customer_import_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_customer_imports/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show CDK Customer Import"
        },
    },

    // Cdk Drive Subscriptions
    {
        path: "/cdk_drive/subscriptions",
        name: "cdk_drive_subscriptions_index",
        // Deprecated: legacy standalone CDK Drive subscription UI.
        // Redirect to integrated Provider Manager.
        redirect: { name: 'subscriptions_provider_manager', query: { tab: 'cdk_drive' } },
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER ],
        },
    },
    {
        path: "/cdk_drive/subscriptions/new",
        name: "cdk_drive_subscriptions_new",
        // Deprecated: legacy standalone CDK Drive subscription UI.
        // Redirect to integrated Provider Manager.
        redirect: { name: 'subscriptions_provider_manager', query: { tab: 'cdk_drive' } },
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER ],
        },
    },
    {
        path: "/cdk_drive/subscriptions/:subscription_vid/edit",
        name: "cdk_drive_subscriptions_edit",
        // Deprecated: legacy standalone CDK Drive subscription UI.
        // Redirect to the generic Subscription detail page.
        redirect: (to) => ({ name: 'subscriptions_edit', params: { vid: to.params.subscription_vid } }),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER ],
        },
    },

    // CDK Drive Runs
    {
        path: "/cdk_drive/runs/:vid/show",
        name: "cdk_drive_run_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_drive_runs/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "CDK Drive Run"
        },
    },

    // CDK Plugins
    {
        path: "/cdk_plugins",
        name: "cdk_plugins_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_plugins/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "CDK Plugins",
            recordVisits: true,
        },
    },
    {
        path: "/cdk_plugins/new",
        name: "cdk_plugins_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_plugins/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "New CDK Plugin"
        },
    },
    {
        path: "/cdk_plugins/:cdk_plugin_key/edit",
        name: "cdk_plugins_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_plugins/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Edit CDK Plugin"
        },
    },

    // CDK Plugin Summaries
    {
        path: "/cdk_3pa_summaries",
        name: "cdk_3pa_summary_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_3pa_summaries/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CDK 3pa Summaries",
            recordVisits: true,
        },
    },

    // Cdk Sale Imports
    {
        path: "/cdk_sale_imports",
        name: "cdk_sale_import_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_sale_imports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CDK Sale Imports",
            recordVisits: true,
        },
    },
    {
        path: "/cdk_sale_imports/:import_id",
        name: "cdk_sale_import_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_sale_imports/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show CDK Sale Import"
        },
    },

    // Cdk Service Header Imports
    {
        path: "/cdk_service_header_imports",
        name: "cdk_service_header_import_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_service_header_imports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CDK Service Header Imports",
            recordVisits: true,
        },
    },
    {
        path: "/cdk_service_header_imports/:import_id",
        name: "cdk_service_header_import_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_service_header_imports/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show CDK Service Header Import"
        },
    },

    // Cdk Service Line Imports
    {
        path: "/cdk_service_line_imports",
        name: "cdk_service_line_import_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_service_line_imports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CDK Service Line Imports",
            recordVisits: true,
        },
    },
    {
        path: "/cdk_service_line_imports/:import_id",
        name: "cdk_service_line_import_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/cdk_service_line_imports/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show CDK Service Line Import"
        },
    },

    // Client User Drops
    {
        path: "/user_client_drops",
        name: "client_user_drops_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/user_client_drops/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_AGENT, Role.ORGANIZATION_MANAGER],
            title: "Client User Drops",
            recordVisits: true,
        },
    },

    // Communication Center
    {
        path: "/communication_center",
        name: "communication_center",
        component: () => import( /* webpackChunkName: "group-communication-center" */ '../views/communication_center/main.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.AGENT, Role.SUPERVISOR, Role.ORGANIZATION_AGENT, Role.ORGANIZATION_MANAGER],
            title: "Communication Center",
            recordVisits: true,
        },
    },

    // Communication Dispositions
    {
        path: "/communication_dispositions",
        name: "communication_dispositions_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/communication_dispositions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Communication Dispositions",
            recordVisits: true,
        },
    },

    // Communication Queue Groups
    {
        path: "/communication_queue_groups",
        name: "communication_queue_groups_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/communication_queue_groups/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Communication Queue Groups",
            recordVisits: true,
        },
    },
    {
        path: "/communication_queue_groups/new",
        name: "communication_queue_groups_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/communication_queue_groups/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Communication Queue Group"
        },
    },
    {
        path: "/communication_queue_groups/:vid/edit",
        name: "communication_queue_groups_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/communication_queue_groups/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Communication Queue Group"
        },
    },

    // Communication Queue User Sessions
    {
        path: "/communication_queue_user_sessions",
        name: "communication_queue_user_sessions_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/communication_queue_user_sessions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Communication Queue User Sessions",
            recordVisits: true,
        },
    },

    // Compliance
    {
        path: "/compliance/likely_opt_outs",
        name: "likely_opt_outs",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/likely_opt_outs/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true
        },
    },
    {
        path: "/compliance/likely_opt_outs/:messageBody",
        name: "likely_opt_outs_filtered",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/likely_opt_outs/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true
        },
    },
    {
        path: "/compliance/opt_out_keywords",
        name: "opt_out_keywords",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/compliance/opt_out_keywords/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true
        },
    },

    // Conferences
    {
        path: "/conferences/:conference_key",
        name: "conference_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/conferences/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Conference"
        },
    },

    // Connect Keys
    {
        path: "/connect_keys",
        name: "connect_keys_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/connect_keys/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Connect Keys",
            recordVisits: true,
        },
    },
    {
        path: "/connect_keys/new",
        name: "connect_keys_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/connect_keys/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "New Connect Key"
        },
    },
    {
        path: "/connect_keys/:connect_key_key/edit",
        name: "connect_keys_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/connect_keys/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Edit Connect Key"
        },
    },

    // Conversica campaign mappings
    {
        path: "/conversica_campaign_mappings",
        name: "conversica_campaign_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/conversica_campaign_mapping/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Conversica Campaign Mappings",
            recordVisits: true,
        },
    },
    {
        path: "/conversica_campaign_mappings/new",
        name: "conversica_campaign_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/conversica_campaign_mapping/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "New Conversica Campaign Mapping"
        },
    },
    {
        path: "/conversica_campaign_mappings/:mapping_key/edit",
        name: "conversica_campaign_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/conversica_campaign_mapping/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Edit Conversica Campaign Mapping"
        },
    },

    // Customers
    {
        path: "/customers/:customer_key",
        name: "customers_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/customers/show.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Show Customer"
        },
    },
    {
        path: "/customer_search",
        name: "customer_search_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/customer_search/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Customer Search",
            recordVisits: true,
        },
    },

    // Dashboards
    {
        path: "/dashboards/affinity-dashboard",
        name: "dashboards_affinity_dashboard",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/affinity_dashboard.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Affinity Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/dashboards/appointment-dashboard",
        name: "dashboards_appointment_dashboard",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/appointment_dashboard.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Appointment Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/dashboards/lead-dashboard",
        name: "dashboards_lead_dashboard",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/lead_dashboard.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Lead Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/dashboards/lead-dashboard/subaru",
        name: "dashboards_lead_dashboard_subaru",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/lead_dashboard_subaru.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Subaru Lead Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/dashboards/lead-dashboard/onstar",
        name: "dashboards_lead_dashboard_onstar",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/lead_dashboard_onstar.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Onstar Lead Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/dashboards/live-dashboard",
        name: "dashboards_live_dashboard",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/live_dashboard.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Live Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/dashboards/big-board",
        name: "dashboards_big_board",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dashboards/big_board.vue'),
        meta: {
            hideMainMenu: true,
            hideTopNav: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Big Board",
            recordVisits: true,
        },
    },
    // Data Imports
    {
        path: "/data_imports",
        name: "data_imports_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/data_imports/index.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/data-imports"),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Data Imports",
        },
    },
    {
        path: "/data_imports/new",
        name: "data_imports_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/data_imports/new.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/data-imports/new"),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Data Import"
        },
    },
    {
        path: "/data_imports/:vid",
        name: "data_imports_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/data_imports/show.vue'),
        beforeEnter: redirectToPulseV2((to) => "/admin/data-imports/" + to.params.vid),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Data Import"
        },
    },
    // Fortellis Reconciliation
    {
        path: "/fortellis_reconciliations",
        name: "fortellis_reconciliation_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/fortellis_reconciliation/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Fortellis Subscription Reports",
        },
    },
    {
        path: "/fortellis_reconciliations/:vid",
        name: "fortellis_reconciliation_review",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/fortellis_reconciliation/review.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Fortellis Reconciliation Review",
        },
    },
    {
        path: "/fortellis_reconciliations/:vid/summary",
        name: "fortellis_reconciliation_summary",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/fortellis_reconciliation/summary.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Fortellis Reconciliation Summary",
        },
    },
    // Data Export Templates
    {
        path: "/data_export_templates",
        name: "data_export_templates_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/data_export_templates/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Data Export Templates",
            recordVisits: true,
        },
    },
    {
        path: "/data_export_templates/:vid/edit",
        name: "data_export_templates_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/data_export_templates/edit.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER ],
            title: "Edit Data Export Template"
        },
    },

    // Data Import Templates
    {
        path: "/data_import_templates",
        name: "data_import_templates_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/data_import_templates/index.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/data-import-templates"),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Data Import Templates",
        },
    },

    //Deal Types
    {
        path: "/deal_types",
        name: "deal_types_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/deal_types/index.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/deal-types", FeatureFlags.DealTypesReactAdmin),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Deal Types",
            recordVisits: true,
        },
    },
    {
        path: "/deal_types/new",
        name: "deal_type_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/deal_types/new.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/deal-types", FeatureFlags.DealTypesReactAdmin),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Deal Type"
        },
    },
    {
        path: "/deal_types/:vid/edit",
        name: "deal_type_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/deal_types/edit.vue'),
        beforeEnter: redirectToPulseV2(() => "/admin/deal-types", FeatureFlags.DealTypesReactAdmin),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Deal Type"
        },
    },

    // Deletables
    {
        path: "/deletion_manager",
        name: "deletables_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/deletables/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Deletables",
            recordVisits: true,
        },
    },

    // Dispositions
    {
        path: "/dispositions",
        name: "dispositions_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dispositions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Dispositions",
            recordVisits: true,
        },
    },
    {
        path: "/dispositions/new",
        name: "dispositions_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dispositions/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Disposition"
        },
    },
    {
        path: "/dispositions/:disposition_key/edit",
        name: "dispositions_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/dispositions/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Disposition"
        },
    },

    // Disposition Groups
    {
        path: "/disposition_groups",
        name: "disposition_groups_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/disposition_groups/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Disposition Groups",
            recordVisits: true,
        },
    },
    {
        path: "/disposition_groups/new",
        name: "disposition_groups_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/disposition_groups/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Disposition Group"
        },
    },
    {
        path: "/disposition_groups/:vid/edit",
        name: "disposition_groups_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/disposition_groups/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Disposition Group"
        },
    },

    // Elead activities
    {
        path: "/elead/subscriptions/:elead_subscription_vid/activities",
        name: "elead_activities_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_activities/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Activities"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/opportunities/:elead_opportunity_vid/activities/new",
        name: "elead_activities_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_activities/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Elead Activity"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/opportunities/:elead_opportunity_vid/activities/:vid/complete",
        name: "elead_activities_complete",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_activities/complete.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Complete Elead Activity"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/opportunities/:elead_opportunity_vid/activities/:vid",
        name: "elead_activity_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_activities/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Elead Activity"
        },
    },

    // Elead activity outcomes
    {
        path: "/elead/subscriptions/:elead_subscription_vid/activity_outcomes",
        name: "elead_outcomes_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_activity_outcomes/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Activity Outcomes"
        },
    },

    // Elead activity types
    {
        path: "/elead/subscriptions/:elead_subscription_vid/activity_types",
        name: "elead_activity_types_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_activity_types/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Activity Types"
        },
    },

    // Elead crm disposition mappings
    {
        path: "/elead/subscriptions/:elead_subscription_vid/organization_plugins/:organization_plugin_vid/crm_disposition_mapping",
        name: "elead_crm_disposition_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead CRM Disposition Mappings"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/organization_plugins/:organization_plugin_vid/crm_disposition_mapping/new",
        name: "elead_crm_disposition_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Elead CRM Disposition Mapping"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/organization_plugins/:organization_plugin_vid/crm_disposition_mapping/:vid/edit",
        name: "elead_crm_disposition_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Elead CRM Disposition Mapping"
        },
    },

    // Elead customers
    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/customers",
        name: "elead_customers_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_customers/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Customers"
        },
    },

    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/customers/new",
        name: "elead_customers_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_customers/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Elead Customer"
        },
    },

    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/customers/:vid/edit",
        name: "elead_customers_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_customers/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Elead Customer"
        },
    },

    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/customers/:vid",
        name: "elead_customers_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_customers/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Elead Customer"
        },
    },

    // Elead emails
    {
        path: "/elead/subscriptions/:elead_subscription_vid/emails",
        name: "elead_emails_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_emails/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Emails"
        },
    },

    // Elead employees
    {
        path: "/elead/subscriptions/:elead_subscription_vid/employees",
        name: "elead_employees_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_employees/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Employees"
        },
    },

    // Elead employee mapping

    {

        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/employee_mapping",
        name: "elead_employee_user_mapping_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_employee_user_mapping/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Employee User Mappings"
        },
    },

    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/employee_mapping/new",
        name: "elead_employee_user_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_employee_user_mapping/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Elead Employee User Mapping"
        },
    },

    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations/:organization_vid/employee_mapping/:vid/edit",
        name: "elead_employee_user_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_employee_user_mapping/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Elead Employee User Mapping"
        },
    },

    // Elead opportunities
    {
        path: "/elead/subscriptions/:elead_subscription_vid/opportunities",
        name: "elead_opportunities_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_opportunities/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Opportunities"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/opportunities/new",
        name: "elead_opportunities_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_opportunities/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Elead Opportunity"
        },
    },
    {
        path: "/elead/subscriptions/:elead_subscription_vid/opportunities/:vid",
        name: "elead_opportunities_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_opportunities/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Elead Opportunity"
        },
    },

    // Elead organizations
    {
        path: "/elead/subscriptions/:elead_subscription_vid/organizations",
        name: "elead_organizations_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_organizations/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Organizations"
        },
    },

    // Elead positions
    {
        path: "/elead/subscriptions/:elead_subscription_vid/positions",
        name: "elead_positions_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_positions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Positions"
        },
    },

    // Elead runs
    {
        path: "/elead/runs",
        name: "elead_runs_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_runs/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Runs",
            recordVisits: true,
        },
    },

    // Elead sales steps
    {
        path: "/elead/subscriptions/:elead_subscription_vid/sales_steps",
        name: "elead_sales_steps_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_sales_steps/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Sales Steps"
        },
    },

    // Elead sources
    {
        path: "/elead/subscriptions/:elead_subscription_vid/sources",
        name: "elead_sources_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_sources/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Sources"
        },
    },

    // Elead statuses
    {
        path: "/elead/subscriptions/:elead_subscription_vid/statuses",
        name: "elead_statuses_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_statuses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Statuses"
        },
    },

    // Elead sub sources
    {
        path: "/elead/subscriptions/:elead_subscription_vid/sub_sources",
        name: "elead_sub_sources_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_sub_sources/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Sub Sources"
        },
    },

    // Elead subscriptions
    {
        path: "/elead/subscriptions/new",
        name: "elead_subscription_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_subscriptions/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Elead Subscription"
        },
    },
    {
        path: "/elead/subscriptions",
        name: 'elead_subscription_index',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_subscriptions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Subscriptions",
            recordVisits: true,
        },
    },
    {
        path: "/elead/subscriptions/:vid",
        name: 'elead_subscription_show',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_subscriptions/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Elead Subscription"
        },
    },
    {
        path: "/elead/subscriptions/:vid/edit",
        name: "elead_subscription_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_subscriptions/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Elead Subscription"
        },
    },

    // Elead vehicle classes
    {
        path: "/elead/subscriptions/:elead_subscription_vid/vehicle_classes",
        name: "elead_vehicle_classes_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_vehicle_classes/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Vehicle Classes"
        },
    },

    // Elead vehicle makes
    {
        path: "/elead/subscriptions/:elead_subscription_vid/vehicle_makes",
        name: "elead_vehicle_makes_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_vehicle_makes/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Vehicle Makes"
        },
    },

    // Elead vehicle models
    {
        path: "/elead/subscriptions/:elead_subscription_vid/vehicle_models",
        name: "elead_vehicle_models_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_vehicle_models/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Vehicle Models"
        },
    },

    // Elead vehicle years
    {
        path: "/elead/subscriptions/:elead_subscription_vid/vehicle_years",
        name: "elead_vehicle_years_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/elead_vehicle_years/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Elead Vehicle Years"
        },
    },


    // Emails
    {
        path: "/emails",
        name: "email_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/emails/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Emails",
            recordVisits: true,
        },
    },
    {
        path: "/emails/:email_key",
        name: "email_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/emails/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Email"
        },
    },

    // Email Templates
    {
        path: "/email_templates",
        name: "email_template_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/email_templates/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Email Templates",
            recordVisits: true,
        },
    },
    {
        path: "/email_templates/new",
        name: "email_template_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/email_templates/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Email Template"
        },
    },
    {
        path: "/email_templates/:vid/edit",
        name: "email_template_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/email_templates/edit.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Email Template"
        },
    },
    // Employees
    {
        path: "/employees",
        name: "employee_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/employees/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Employees",
            recordVisits: true,
        },
    },
    {
        path: "/employees/:vid/show",
        name: "employee_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/employees/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Employee"
        },
    },

    // Enroller Runs
    {
        path: "/enroller_runs",
        name: "enroller_runs_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/enroller_runs/index.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Enroller Runs",
            recordVisits: true,
        },
    },
    {
        path: "/enroller_runs/:vid/:step?",
        name: "enroller_runs_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/enroller_runs/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Enroller Run"
        },
    },

    // Goal Templates
    {
        path: "/goal_templates",
        name: "goal_templates_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/goal_templates/index.vue'),
        meta: {
            hideMainMenu: true,
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Goal Templates",
            recordVisits: true,
        },
    },
    {
        path: "/goal_templates/new",
        name: "goal_templates_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/goal_templates/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Goal Template"
        },
    },
    {
        path: "/goal_templates/:vid/edit",
        name: "goal_templates_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/goal_templates/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Goal Template"
        },
    },

    // Incoming Phone Numbers
    {
        path: "/incoming_phone_numbers",
        name: "incoming_phone_numbers_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/incoming_phone_numbers/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Incoming Phone Numbers",
            recordVisits: true,
        },
    },
    {
        path: "/incoming_phone_numbers/new",
        name: "incoming_phone_numbers_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/incoming_phone_numbers/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Incoming Phone Number"
        },
    },
    {
        path: "/incoming_phone_numbers/:incoming_phone_number_key/edit",
        name: "incoming_phone_numbers_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/incoming_phone_numbers/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Incoming Phone Number"
        },
    },

    // Incoming Phone Number Orders
    {
        path: "/incoming_phone_number_orders/:incoming_phone_number_order_key",
        name: "incoming_phone_number_orders_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/incoming_phone_number_orders/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Incoming Phone Number Order"
        },
    },

    // IVR
    {
        path: "/ivr_trees",
        name: "ivr_trees_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/ivr_trees/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "IVR Trees",
            recordVisits: true,
        },
    },
    {
        path: "/ivr_trees/:id/edit",
        name: "ivr_trees_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/ivr_trees/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            hideMainMenu: true,
            title: "Edit IVR Tree"
        },
    },
    {
        path: "/ivr_trees/new",
        name: "ivr_trees_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/ivr_trees/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New IVR Tree"
        },
    },

    // Job Titles
    {
        path: "/job_titles",
        name: "job_titles_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/job_titles/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Job Titles",
            recordVisits: true,
        },
    },
    {
        path: "/job_titles/new",
        name: "job_titles_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/job_titles/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Job Title"
        },
    },
    {
        path: "/job_titles/:vid/edit",
        name: "job_titles_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/job_titles/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Job Title"
        },
    },

    // Logs
    {
        path: "/logs",
        name: "logs_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/logs/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Logs",
            recordVisits: true,
        },
    },

    //llm_assessments
    {
        path: "/llm_assessments/:vid",
        name: "llm_assessments_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/llm_assessments/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            hideMainMenu: true,
        },
    },

    // Messages
    {
        path: "/messages",
        name: "messages_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/messages/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Messages",
            recordVisits: true,
        },
    },
    {
        path: "/messages/:vid",
        name: "message_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/messages/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Message"
        },
    },

    // Message Templates
    {
        path: "/message_templates",
        name: "message_templates_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/message_templates/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Message Templates",
            recordVisits: true,
        },
    },
    {
        path: "/message_templates/new",
        name: "message_templates_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/message_templates/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Message Template"
        },
    },
    {
        path: "/message_templates/:vid/edit",
        name: "message_templates_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/message_templates/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Message Template"
        },
    },

    // Missed Calls
    {
        path: "/missed_calls",
        name: "missed_call_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/voice_mail_recordings/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Missed Calls",
            recordVisits: true,
        },
    },

    // OEMs
    {
        path: "/oems",
        name: "oems_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/oems/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "OEMs",
            recordVisits: true,
        }
    },

    // Organizations CRUD
    {
        path: "/organizations",
        name: "organizations_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organizations/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Organizations",
            recordVisits: true,
        }
    },
    {
        path: "/organizations/new",
        name: "organizations_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organizations/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "New Organization"
        }
    },
    {
        path: "/organizations/:vid/edit",
        name: "organizations_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organizations/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Edit Organization"
        }
    },

    // Organizations vin solution
    {
        path: "/organizations_account_plugin",
        name: "organizations_account_plugin_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organizations_vin_solution/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organizations VIN Solution",
            recordVisits: true,
        },
    },

    // Organizations mappings
    {
        path: "/organization_mappings",
        name: "organization_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_mappings/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organization Mappings",
            recordVisits: true,
        },
    },
    {
        path: "/organization_mappings/new",
        name: "organization_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_mappings/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Organization Mapping"
        },
    },
    {
        path: "/organization_mappings/:mapping_id/edit",
        name: "organization_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_mappings/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Organization Mapping"
        },
    },

    // Organization Plugins
    {
        path: "/organization_plugins",
        name: "organization_plugin_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Organization Plugins",
            recordVisits: true,
        },
    },
    {
        path: "/organization_plugins/new",
        name: "organization_plugin_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Organization Plugin"
        },
    },
    {
        path: "/organization_plugins/:organization_plugin_vid/edit",
        name: "organization_plugin_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Organization Plugin"
        },
    },

    // Organization Plugin Mappings
    // Promax Mappings
    {
        path: "/organization_plugins/:organization_plugin_vid/promax_mappings",
        name: "organization_plugin_promax_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organization Plugin Promax Mappings"
        },
    },

    {
        path: "/organization_plugins/:organization_plugin_vid/promax_mapping/new",
        name: "organization_plugin_promax_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Organization Plugin Promax Mapping"
        },
    },

    {
        path: "/organization_plugins/:organization_plugin_vid/promax_mapping/:vid/edit",
        name: "organization_plugin_promax_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Organization Plugin Promax Mapping"
        },
    },
    // Subaru Mappings
    {
        path: "/organization_plugins/:organization_plugin_vid/subaru_mappings",
        name: "organization_plugin_subaru_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organization Plugin Subaru Mappings"
        },
    },

    {
        path: "/organization_plugins/:organization_plugin_vid/subaru_mapping/new",
        name: "organization_plugin_subaru_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Organization Plugin Subaru Mapping"
        },
    },

    {
        path: "/organization_plugins/:organization_plugin_vid/subaru_mapping/:vid/edit",
        name: "organization_plugin_subaru_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Organization Plugin Subaru Mapping"
        },
    },
    // Vin Solutions Mappings
    {
        path: "/organization_plugins/:organization_plugin_vid/vin_solution_mappings",
        name: "organization_plugin_vin_solution_mappings_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organization Plugin Vin Solution Mappings"
        },
    },

    {
        path: "/organization_plugins/:organization_plugin_vid/vin_solution_mappings/new",
        name: "organization_plugin_vin_solution_mappings_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Organization Plugin Vin Solution Mapping"
        },
    },

    {
        path: "/organization_plugins/:organization_plugin_vid/vin_solution_mappings/:vid/edit",
        name: "organization_plugin_vin_solution_mappings_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_plugins/crm_disposition_mapping/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Organization Plugin Vin Solution Mapping"
        },
    },

    // Organizations postal codes
    {
        path: "/organization_postal_codes",
        name: "organization_postal_codes_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_postal_codes/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organization Postal Codes",
            recordVisits: true,
        },
    },
    {
        path: "/organization_postal_codes/new",
        name: "organization_postal_codes_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_postal_codes/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Organization Postal Code"
        },
    },
    {
        path: "/organization_postal_codes/:vid/edit",
        name: "organization_postal_codes_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/organization_postal_codes/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Organization Postal Code"
        },
    },

    // Outgoing Caller IDs

    {
        path: "/outgoing_caller_ids",
        name: "outgoing_caller_ids_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/outgoing_caller_ids/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Outgoing Caller IDs",
            recordVisits: true,
        },
    },

    // Priority Management

    {
        path: "/priority_groups",
        name: "priority_groups_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/priority_groups/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER ],
            title: "Priority Groups",
            recordVisits: true,
        },
    },

    {
        path: "/priority_groups/:vid",
        name: "priority_groups_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/priority_groups/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER ],
            title: "Show Priority Group"
        },
    },

    // Reports
    {
        path: "/reports",
        name: "reports_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
        },
    },
    {
        path: "/reports/glossary",
        name: "reports_glossary",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/glossary.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            title: "Glossary / Formulas",
        },
    },
    {
        path: "/reports/:vid",
        name: "reports_view",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
        },
    },
    {
        path: "/agent_efficiency_report",
        name: "agent_efficiency_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/agent_efficiency_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Agent Efficiency Report",
            recordVisits: true,
        },
    },
    {
        path: "/appointment_compensation_report",
        name: "appointment_compensation_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/appointment_compensation_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Appointment Compensation Report",
            recordVisits: true,
        },
    },
    {
        path: "/call_results_report",
        name: "call_results_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/call_results_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Call Results Report",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_appointment_compensation_report",
        name: "campaign_appointment_compensation_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/appointment_compensation_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Campaign Appointment Compensation Report",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_follow_up_report",
        name: "campaign_follow_up_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/campaign_follow_up_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            hideMainMenu: true,
            title: "Campaign Follow Up Report",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_performance_report",
        name: "campaign_performance_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/campaign_performance_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Campaign Performance Report",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_summary_report",
        name: "campaign_summary_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/campaign_summary_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Campaign Summary Report",
            recordVisits: true,
        },
    },
    {
        path: "/campaign_time_report",
        name: "campaign_time_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/campaign_time_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Campaign Time Report",
            recordVisits: true,
        },
    },
    {
        path: "/communication_disposition_report",
        name: "communication_disposition_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/communication_disposition_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Communication Disposition Report",
            recordVisits: true,
        },
    },
    {
        path: "/daily_import_report",
        name: "daily_import_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/daily_import_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Daily Import Report",
            recordVisits: true,
        },
    },
    {
        path: "/email_summary_report",
        name: "email_summary_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/email_summary_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Email Summary Report",
            recordVisits: true,
        },
    },
    {
        path: "/heat_report",
        name: "heat_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/heat_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER, Role.CLIENT_EMPLOYEE],
            hideMainMenu: true,
            title: "Heat Report",
            recordVisits: true,
        },
    },
    {
        path: "/performance_report",
        name: "performance_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/performance_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Performance Report",
            recordVisits: true,
        },
    },
    {
        path: "/roi_report",
        name: "roi_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reports/roi_report.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "ROI Report",
            recordVisits: true,
        },
    },
    {
        path: "/support_request_analysis_report",
        name: "support_request_analysis_report",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/utilities/support_request_analysis_report/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            hideMainMenu: true,
            title: "Support Request Analysis Report",
            recordVisits: true,
        },
    },

    // Reyonlds RCI log, DMS Data
    {
        path: "/reynolds_rci_logs",
        name: "reynolds_rci_log_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reynolds_rci_logs/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Reynolds RCI Logs",
            recordVisits: true,
        },
    },
    {
        path: "/reynolds_rci_logs/:rci_log_id",
        name: "reynolds_rci_log_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/reynolds_rci_logs/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Reynolds RCI Log"
        },
    },

    // Scorecards
    {
        path: "/scorecards",
        name: "scorecards",
        redirect: () => {
            return "/scorecards/inbox";
        },
        meta: {
            allowedRoles: [Role.All],
            redirect: true,
            title: "Scorecards",
            recordVisits: true,
        },
    },
    {
        path: "/scorecards/inbox",
        name: "scorecards_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/scorecards/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Scorecards Inbox",
            recordVisits: true,
        },
    },
    {
        path: "/scorecards/dashboard",
        name: "scorecard_reports_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/scorecards/reports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Scorecards Dashboard",
            recordVisits: true,
        },
    },
    {
        path: "/scorecards/templates",
        name: "scorecard_templates_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/scorecard_templates/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Scorecards Templates",
            recordVisits: true,
        },
    },
    {
        path: "/scorecards/templates/:vid/edit",
        name: "scorecard_templates_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/scorecard_templates/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            hideMainMenu: true,
            title: "Edit Scorecard Template"
        },
    },
    {
        path: "/scorecards/inbox/:vid",
        name: "scorecards_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/scorecards/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Show Scorecard"
        },
    },

    // Subscriptions
    {
        path: "/subscriptions",
        name: 'subscriptions_index',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/subscriptions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Subscriptions",
            recordVisits: true,
        },
    },
    {
        path: "/crm_subscriptions",
        name: 'crm_subscriptions_index',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/crm_subscriptions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CRM Subscriptions",
            recordVisits: true,
        },
    },
    {
        path: "/crm_subscriptions/:subscription_vid",
        name: 'crm_subscriptions_show',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/crm_subscriptions/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CRM Subscription",
        },
    },
    {
        path: "/crm_organizations/:vid",
        name: 'crm_organizations_show',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/crm_organizations/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "CRM Organization",
        },
    },
    // Deprecated: legacy Click-to-Call paths renamed to CRM. Redirect old bookmarks to the new routes.
    {
        path: "/click_to_call_subscriptions",
        redirect: { name: 'crm_subscriptions_index' },
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
        },
    },
    {
        path: "/click_to_call_subscriptions/:subscription_vid",
        redirect: (to) => ({ name: 'crm_subscriptions_show', params: { subscription_vid: to.params.subscription_vid } }),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
        },
    },
    {
        path: "/click_to_call_organizations/:vid",
        redirect: (to) => ({ name: 'crm_organizations_show', params: { vid: to.params.vid } }),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
        },
    },
    {
        path: "/subscriptions/new",
        name: 'subscriptions_new',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/subscriptions/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Subscription"
        },
    },
    {
        path: "/subscriptions/:vid/edit",
        name: 'subscriptions_edit',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/subscriptions/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Subscription"
        },
    },
    {
        path: "/subscriptions/provider_manager",
        name: 'subscriptions_provider_manager',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/subscriptions_manager/main.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Subscription Provider Manager",
            recordVisits: true,
        },
    },


    // Surveys
    {
        path: "/surveys",
        name: "surveys_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/surveys/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Surveys",
            recordVisits: true,
        },
    },
    {
        path: "/surveys/:vid",
        name: "survey_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/surveys/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.SUPERVISOR, Role.ORGANIZATION_MANAGER],
            title: "Show Survey"
        },
    },

    // Survey Templates
    {
        path: "/survey_templates",
        name: "survey_template_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/survey_templates/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Survey Templates",
            recordVisits: true,
        },
    },
    {
        path: "/survey_templates/new",
        name: "survey_template_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/survey_templates/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Survey Template"
        },
    },
    {
        path: "/survey_templates/:vid/edit",
        name: "survey_template_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/survey_templates/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Survey Template"
        },
    },

    // Survey Template Questions
    {
        path: "/survey_templates/:survey_template_vid/questions/new",
        name: "survey_template_question_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/survey_template_questions/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Survey Template Question"
        },
    },
    {
        path: "/survey_templates/:survey_template_vid/questions/edit/:id",
        name: "survey_template_question_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/survey_template_questions/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Survey Template Question"
        },
    },
    // Survey Attempts
    {
        path: "/survey_attempts",
        name: 'survey_attempts_index',
        component: () => import( /* webpackChunkName: "group-app" */ '../views/survey_attempts/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Survey Attempts",
            recordVisits: true,
        },
    },

    // Tags
    {
        path: "/tags",
        name: "tag_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tags/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tags",
            recordVisits: true,
        },
    },
    {
        path: "/tags/new",
        name: "tag_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tags/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Tag"
        },
    },
    {
        path: "/tags/:tag_key/edit",
        name: "tag_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tags/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Tag"
        },
    },

    // Teams
    {
        path: "/teams",
        name: "teams_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/teams/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Teams",
            recordVisits: true,
        },
    },
    {
        path: "/teams/new",
        name: "teams_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/teams/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Team"
        },
    },
    {
        path: "/teams/:vid/edit",
        name: "teams_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/teams/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Team"
        },
    },

    // Tekion Appointments
    {
        path: "/tekion_appointments",
        name: "tekion_appointments_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_appointments/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Appointments",
            recordVisits: true,
        },
    },
    {
        path: "/tekion_appointments/:tekion_appointment_key",
        name: "tekion_appointments_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_appointments/detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Tekion Appointment"
        }
    },

    // Tekion Customers
    {
        path: "/tekion_customers",
        name: "tekion_customers_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_customers/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Customers",
            recordVisits: true,
        },
    },

    // Tekion Deals
    {
        path: "/tekion_deals",
        name: "tekion_deals_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_deals/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Deals",
            recordVisits: true,
        },
    },
    {
        path: "/tekion_deals/:tekion_deal_key",
        name: "tekion_deals_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_deals/detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Tekion Deal"
        }
    },

    // Tekion Employees
    {
        path: "/tekion_employees",
        name: "tekion_employees_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_employees/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Employees",
            recordVisits: true,
        },
    },

    // Tekion Opcodes
    {
        path: "/tekion_opcodes",
        name: "tekion_opcodes_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_opcodes/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Opcodes",
            recordVisits: true,
        },
    },
    {
        path: "/tekion_opcodes/:tekion_opcode_key",
        name: "tekion_opcodes_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_opcodes/detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Tekion Opcode"
        }
    },

    // Tekion Vehicle Inventories
    {
        path: "/tekion_vehicle_inventories",
        name: "tekion_vehicle_inventories_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_vehicle_inventories/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Vehicle Inventories",
            recordVisits: true,
        },
    },
    {
        path: "/tekion_vehicle_inventories/:tekion_vehicle_inventory_key",
        name: "tekion_vehicle_inventories_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_vehicle_inventories/detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Tekion Vehicle Inventory"
        }
    },

    // Tekion Vehicles
    {
        path: "/tekion_vehicles",
        name: "tekion_vehicles_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_vehicles/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Vehicles",
            recordVisits: true,
        },
    },
    {
        path: "/tekion_vehicles/:tekion_vehicle_key",
        name: "tekion_vehicles_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_vehicles/detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Tekion Vehicle"
        }
    },

    // Tekion Transportation
    {
        path: "/tekion_transportation",
        name: "tekion_transportation_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tekion_transportation/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Tekion Transportation",
            recordVisits: true,
        },
    },

    // Telematic Alerts
    {
        path: "/telematic_alerts",
        name: "telematic_alerts_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/telematic_alerts/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Telematic Alerts",
            recordVisits: true,
        },
    },

    // Tickets
    {
        path: "/tickets",
        name: "ticket_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tickets/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            forceNavbarSize: { isMini: true },
            title: "Tickets",
            recordVisits: true,
        },
    },
    {
        path: "/tickets/new",
        name: "ticket_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tickets/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "New Ticket"
        },
    },
    {
        path: "/tickets/:ticket_key",
        name: "ticket_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/tickets/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Show Ticket"
        },
    },
    {
        path: "/ticket_assigners",
        name: "ticket_assigner_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/ticket_assigners/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Ticket Assigners",
            recordVisits: true,
        },
    },

    // Trust Hub
    {
        path: "/trust",
        name: "trust",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/trust/main.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Trust Hub",
            recordVisits: true,
        },
    },
    {
        path: "/trust/business_profile/:vid",
        name: "trust_business_profile_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/trust/business_profile/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Business Profile",
            recordVisits: true,
        },
    },
    {
        path: "/trust/business_profile/:business_profile_vid/organization/:business_profile_organization_vid",
        name: "business_profile_organizations_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/trust/business_profile/organization_edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            hideMainMenu: true,
            title: "Business Profile Organization",
            recordVisits: true,
        },
    },

    // Users
    {
        path: "/users",
        name: "users_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/users/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Users",
            recordVisits: true,
        },
    },
    {
        path: "/users/new",
        name: "users_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/users/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New User"
        },
    },
    {
        path: "/users/:vid/edit",
        name: "users_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/users/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            hideMainMenu: true,
            title: "Edit User"
        },
    },

    // Incoming Phone Number Ports (super admin, RESTful)
    {
        path: "/incoming_phone_number_ports",
        name: "incoming_phone_number_ports_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/incoming_phone_number_ports/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Incoming Phone Number Ports",
            recordVisits: true,
        },
    },
    {
        path: "/incoming_phone_number_ports/:vid",
        name: "incoming_phone_number_ports_show",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/incoming_phone_number_ports/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Incoming Phone Number Port File"
        },
    },

    // Us Federal DNC Files
    {
        path: "/us_federal_dnc_files",
        name: "us_federal_dnc_file_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/us_federal_dnc_files/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "US Federal DNC Files",
            recordVisits: true,
        },
    },
    {
        path: "/us_federal_dnc_files/new",
        name: "us_federal_dnc_file_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/us_federal_dnc_files/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New US Federal DNC File"
        }
    },

    // Vehicle transaction statuses
    {
        path: "/vehicle_transaction_statuses",
        name: "vehicle_transaction_statuses_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vehicle_transaction_statuses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER, Role.ORGANIZATION_MANAGER],
            title: "Vehicle Transaction Statuses",
            recordVisits: true,
        },
    },

    // Vin solutions contacts
    {
        path: "/vin_solution_contacts/:organization_key",
        name: "vin_solution_contacts_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_contacts/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Contacts"
        },
    },
    {
        path: "/vin_solution_contacts/:organization_key/new",
        name: "vin_solution_contacts_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_contacts/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Vin Solutions Contact"
        },
    },

    // Vin solutions dealers
    {
        path: "/vin_solution_dealers",
        name: "vin_solution_dealer_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_dealers/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Vin Solutions Dealers",
            recordVisits: true,
        },
    },
    {
        path: "/vin_solution_dealers/:organization_key/view",
        name: "vin_solution_dealer_view",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_dealers/view.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Vin Solutions Dealer"
        },
    },

    // Vin solutions inventories
    {
        path: "/vin_solution_inventories/:organization_key",
        name: "vin_solution_inventories_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_inventories/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Inventories"
        },
    },

    // Vin solutions lead group categories
    {
        path: "/vin_solution_lead_group_categories/:organization_key",
        name: "vin_solution_lead_group_categories_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_lead_group_categories/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Lead Group Categories"
        },
    },

    // Vin solutions lead sources
    {
        path: "/vin_solution_lead_sources/:organization_key",
        name: "vin_solution_lead_sources_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_lead_sources/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Lead Sources"
        },
    },

    // Vin solutions lead status types
    {
        path: "/vin_solution_lead_status_types/:organization_key",
        name: "vin_solution_lead_status_types_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_lead_status_types/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Lead Status Types"
        },
    },

    // Vin solutions lead statuses
    {
        path: "/vin_solution_lead_statuses/:organization_key",
        name: "vin_solution_lead_statuses_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_lead_statuses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Lead Statuses"
        },
    },

    // Vin solutions lead types
    {
        path: "/vin_solution_lead_types/:organization_key",
        name: "vin_solution_lead_types_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_lead_types/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Lead Types"
        },
    },

    // Vin solutions leads
    {
        path: "/vin_solution_leads/:organization_key",
        name: "vin_solution_leads_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_leads/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Leads"
        },
    },
    {
        path: "/vin_solution_leads/:organization_key/new",
        name: "vin_solution_lead_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_leads/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Vin Solutions Lead"
        },
    },
    {
        path: "/vin_solution_leads/:organization_key/:lead_key/edit",
        name: "vin_solution_lead_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_leads/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Edit Vin Solutions Lead"
        },
    },

    // Vin solutions logs
    {
        path: "/vin_solution_logs/:organization_key",
        name: "vin_solution_logs_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_logs/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Logs"
        },
    },
    {
        path: "/vin_solution_logs/:organization_key/:log_id",
        name: "vin_solution_logs_detail",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_logs/detail.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Show Vin Solutions Log"
        },
    },

    // Vin solutions users
    {
        path: "/vin_solution_users/:organization_key",
        name: "vin_solution_users_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_users/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Users"
        },
    },

    // Vin solutions vehicle makes
    {
        path: "/vin_solution_vehicle_makes/:organization_key",
        name: "vin_solution_vehicle_makes_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_vehicle_makes/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Vehicle Makes"
        },
    },

    // Vin solutions vehicle models
    {
        path: "/vin_solution_vehicle_models/:organization_key",
        name: "vin_solution_vehicle_models_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_vehicle_models/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Vehicle Models"
        },
    },

    // Vin solutions vehicle trims
    {
        path: "/vin_solution_vehicle_trims/:organization_key",
        name: "vin_solution_vehicle_trims_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_vehicle_trims/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Vehicle Trims"
        },
    },

    // Vin solutions vehicle model years
    {
        path: "/vin_solution_vehicle_model_years/:organization_key",
        name: "vin_solution_vehicle_model_years_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/vin_solution_vehicle_model_years/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Vin Solutions Vehicle Model Years"
        },
    },

    // Webhooks
    {
        path: "/webhooks",
        name: "webhooks_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/webhooks/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "Webhooks",
            recordVisits: true,
        },
    },
    {
        path: "/webhooks/new",
        name: "webhooks_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/webhooks/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER],
            title: "New Webhook"
        },
    },
    {
        path: "/webhooks/:vid/edit",
        name: "webhooks_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/webhooks/edit.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN, Role.MANAGER, Role.OWNER ],
            title: "Edit Webhook"
        },
    },

    // White labels
    {
        path: "/white_labels",
        name: "white_label_index",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/white_labels/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "White Labels",
            recordVisits: true,
        },
    },
    {
        path: "/white_labels/new",
        name: "white_label_new",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/white_labels/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New White Label"
        },
    },
    {
        path: "/white_labels/:vid/edit",
        name: "white_label_edit",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/white_labels/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit White Label"
        },
    },

    // Utilities
    {
        path: '/utilities',
        name: 'utilities',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Utilities",
            recordVisits: true,
        },
    },

    // Utilities - Feature Flags
    {
        path: '/utilities/feature_flags',
        name: 'utilities_feature_flags_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/feature_flags/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Feature Flags",
            recordVisits: true,
        },
    },
    {
        path: '/utilities/microsoft_teams_permissions',
        name: 'utilities_microsoft_teams_permissions',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/microsoft_teams_permissions/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Microsoft Teams Permissions",
            recordVisits: true,
        },
    },
    {
        path: '/utilities/feature_flags/new',
        name: 'utilities_feature_flags_new',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/feature_flags/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Feature Flag",
        },
    },
    {
        path: '/utilities/feature_flags/:vid/edit',
        name: 'utilities_feature_flags_edit',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/feature_flags/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Feature Flag",
        },
    },

    {
        path: '/utilities/bad_addresses',
        name: 'utilities_bad_address_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/bad_addresses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Bad Addresses",
            recordVisits: true,
        }
    },
    {
        path: '/utilities/database_maintenance/table_reindex_registry',
        name: 'utilities_table_reindex_registry_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/database_maintenance/table_reindex_registry/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Table Reindex Registry",
            recordVisits: true,
        }
    },

    {
        path: '/utilities/auto_assistant_phone_numbers',
        name: 'utilities_auto_assistant_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/auto_assistant_phone_numbers/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Auto Assistant Phone Numbers",
            recordVisits: true,
        }
    },
    {
        path: '/utilities/bad_email_addresses',
        name: 'utilities_bad_email_address_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/bad_email_addresses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Bad Email Addresses",
            recordVisits: true,
        }
    },
    {
        path: '/utilities/bad_names',
        name: 'utilities_bad_names_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/bad_names/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Bad Names",
            recordVisits: true,
        }
    },
    {
        path: '/utilities/missed_calls',
        name: 'missedCalls',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/communications/missed_calls.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Missed Calls",
            recordVisits: true,
        },
    },
    {
        path: '/utilities/communication_responses',
        name: 'communication_responses_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/communications/communication_responses/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Communication Responses",
            recordVisits: true,
        },
    },
    {
        path: '/utilities/scheduled_communications',
        name: 'scheduledCommunications',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/communications/scheduled_communications.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Scheduled Communications",
            recordVisits: true,
        },
    },

    // Utilities - Bad Records
    {
        path: "/utilities/bad_phone_numbers",
        name: "badPhoneNumbers",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/utilities/bad_records/bad_phone_numbers.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Bad Phone Numbers",
            recordVisits: true,
        },
    },

    // Utilities - Communication Assessments - Scorecard Queues-  Index
    {
        path: '/utilities/scorecard_queues',
        name: 'scorecard_queues_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/scorecard_queues/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Scorecard Queues",
            recordVisits: true,
        },
    },

    // Utilities - A/B Buckets
    {
        path: '/utilities/a_b_buckets',
        name: 'a_b_buckets',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/a_b_buckets/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "A/B Buckets",
            recordVisits: true,
        },
    },

    // Utilities - Workplan Templates
    {
        path: '/utilities/workplan_templates',
        name: 'utilities_workplan_templates',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/workplan_templates/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Workplan Templates",
            recordVisits: true,
        },
    },
    {
        path: '/utilities/workplan_templates/:vid/edit',
        name: 'utilities_workplan_templates_edit',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/workplan_templates/edit.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Edit Workplan Template",
        },
    },

    // Utilities - AI Communication Dispositions
    {
        path: '/utilities/ai_communication_dispositions',
        name: 'ai_communication_dispositions_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/ai_communication_dispositions/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "AI Communication Dispositions",
            recordVisits: true,
        },
    },

    // Utilities - AI Communication Dispositions
    {
        path: '/utilities/ai_communication_dispositions/:vid',
        name: 'ai_communication_dispositions_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/ai_communication_dispositions/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
        },
    },

    // Utilities - Integration App Logins
    {
        path: '/utilities/integration_app_logins',
        name: 'utilities_integration_app_logins',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/integration_app_logins/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Integration App Logins",
            recordVisits: true,
        },
    },

    // Utilities - Integration App Requests
    {
        path: '/utilities/integration_requests',
        name: 'utilities_integration_requests',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/integration_requests/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Integration Requests",
            recordVisits: true,
        },
    },

    // Utilities - Subscription Data Explorer
    {
        path: '/utilities/subscription_data_explorer/search',
        name: 'utilities_subscription_data_explorer_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/subscription_data_explorer/search.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Subscription Data Explorer",
            recordVisits: true,
        },
    },
    {
        path: '/utilities/subscription_data_explorer/imports/:vid',
        name: 'utilities_subscription_data_explorer_import_rows',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/subscription_data_explorer/import_row_explorer.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Import Row Explorer",
        },
    },
    {
        path: '/utilities/subscription_data_explorer/:vid/source_runs',
        name: 'utilities_subscription_data_explorer_source_runs',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/subscription_data_explorer/source_runs.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Source Runs",
        },
    },
    {
        path: '/utilities/subscription_data_explorer/:vid/rejected_rows',
        name: 'utilities_subscription_data_explorer_rejected_rows',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/subscription_data_explorer/rejected_rows.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Rejected Rows",
        },
    },
    {
        path: '/utilities/subscription_data_explorer/:vid',
        name: 'utilities_subscription_data_explorer_organization_imports',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/subscription_data_explorer/organization_imports.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Subscription Imports",
        },
    },

    // Utilities - Phone Number Transfers
    {
        path: '/utilities/phone_number_transfers',
        name: 'phone_number_transfers_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/phone_number_transfers/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Phone Number Transfers",
            recordVisits: true,
        },
    },

    // Utilities - Phone Number Transfers - New
    {
        path: '/utilities/phone_number_transfers/new',
        name: 'phone_number_transfers_new',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/phone_number_transfers/new.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "New Phone Number Transfer",
        },
    },

    // Utilities - Phone Number Transfers - Show
    {
        path: '/utilities/phone_number_transfers/:vid',
        name: 'phone_number_transfers_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/phone_number_transfers/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Phone Number Transfer",
        },
    },

    // Utilities - User Logins with Access Issues
    {
        path: '/utilities/user_logins_with_access_issues',
        name: 'utilities_user_logins_with_access_issues',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/user_logins_with_access_issues/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "User Logins with Access Issues",
            recordVisits: true,
        },
    },

    // Utilities - Telnyx

    {
        path: '/utilities/telnyx',
        name: 'telnyx',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Telnyx",
            recordVisits: true,
        },
    },

    // Utilities - Telnyx - Billing Group Show
    {
        path: '/utilities/telnyx/accounts/:vid',
        name: 'telnyx_billing_groups_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Telnyx Billing Group"
        },
    },

    // Utilities - Telnyx - Billing Groups - Brands - Show
    {
        path: '/accounts/:account_vid/atop_brands/:vid',
        name: 'telnyx_billing_groups_brands_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/brands/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Telnyx Billing Group Brand"
        }
    },

    // Utilities - Telnyx - Billing Groups - Brands - New
    {
        path: '/accounts/:account_vid/brands/new',
        name: 'telnyx_billing_groups_brands_new',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/brands/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Telnyx Billing Group Brand"
        }
    },

    // Utilities - Telnyx - Billing Groups - Brands - Edit
    {
        path: '/accounts/:account_vid/brands/:vid/edit',
        name: 'telnyx_billing_groups_brands_edit',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/brands/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Telnyx Billing Group Brand"
        }
    },

    // Utilities - Telnyx - Billing Groups - Brands - Campaigns - New
    {
        path: '/accounts/:account_vid/brands/:brand_vid/campaigns/new',
        name: 'telnyx_billing_groups_brands_campaigns_new',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/brands/campaigns/new.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "New Telnyx Billing Group Campaign"
        }
    },

    // Utilities - Telnyx - Billing Groups - Brands - Campaigns - Show
    {
        path: '/accounts/:account_vid/brands/:brand_vid/campaigns/:vid',
        name: 'telnyx_billing_groups_brands_campaigns_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/brands/campaigns/show.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Show Telnyx Billing Group Campaign"
        }
    },

    // Utilities - Telnyx - Billing Groups - Brands - Campaigns - Edit
    {
        path: '/accounts/:account_vid/brands/:brand_vid/campaigns/:vid/edit',
        name: 'telnyx_billing_groups_brands_campaigns_edit',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/telnyx/brands/campaigns/edit.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Edit Telnyx Billing Group Campaign"
        }
    },

    // Utilities - Communication Assessments - Entity Detection Matches -  Index
    {
        path: '/utilities/entity_detection_matches',
        name: 'entity_detection_matches_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/entity_detection_matches/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Entity Detection Matches",
            recordVisits: true,
        },
    },

    // Utilities - Communication Assessments - Escalated Customers -  Index
    {
        path: '/utilities/escalated_customers',
        name: 'escalated_customers_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/escalated_customers/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Escalated Customers",
            recordVisits: true,
        },
    },

    // Utilities - Communication Assessments - Escalated Customers -  Show
    {
        path: '/utilities/escalated_customers/:vid',
        name: 'escalated_customers_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/escalated_customers/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Show Escalated Customer"
        },
    },

    // Utilities - Communication Assessments - Entity Detection Matches -  Show
    {
        path: '/utilities/entity_detection_matches/:vid',
        name: 'entity_detection_matches_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/entity_detection_matches/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Entity Detection Match"
        },
    },

    // Utilities - Communication Assessments - LLM Assessments -  Index
    {
        path: '/utilities/llm_assessments',
        name: 'llm_assessments_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/llm_assessments/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "LLM Assessments",
            recordVisits: true,
        },
    },

    // Utilities - LLM Queries - LLM Assessments -  Show
    {
        path: '/utilities/llm_assessments/:vid',
        name: 'llm_assessments_show_super_admin',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/llm_assessments/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Show LLM Assessment"
        },
    },

    // Utilities - LLM Queries - LLM LLM Queries -  Index
    {
        path: '/utilities/llm_queries',
        name: 'llm_queries_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/llm_queries/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "LLM Queries",
            recordVisits: true,
        },
    },

    // Utilities - LLM Queries - LLM LLM Queries -  Show
    {
        path: '/utilities/llm_queries/:id',
        name: 'llm_queries_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/llm_queries/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Show LLM Query"
        },
        },

    // Utilities - Communication Assessments - Outcomes -  Index
    {
        path: '/utilities/outcomes',
        name: 'outcomes_index',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/outcomes/index.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Outcomes",
            recordVisits: true,
        },
    },

    // Utilities - LLM Queries - Outcomes -  Show
    {
        path: '/utilities/outcomes/:vid',
        name: 'outcomes_show',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/llm_queries/outcomes/show.vue'),
        meta: {
            allowedRoles: [ Role.SUPER_ADMIN ],
            title: "Show Outcome"
        },
    },

    // Utitities - Users
    {
        path: "/utilities/user_logging",
        name: "utilities_user_logging_index",
        component: () =>
            import(
          /* webpackChunkName: "group-utilities" */ "../views/utilities/user_logging/index.vue"
            ),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "User Logging",
            recordVisits: true,
        },
    },

    // Utilities - Organization Search
    {
        path: '/utilities/organization_search',
        name: 'utilities_organization_search',
        component: () => import( /* webpackChunkName: "group-utilities" */ '../views/utilities/organization_search/index.vue'),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
            title: "Organization Search",
            recordVisits: true,
        },
    },

    // Utilities - User Account Cleanup
    {
        path: "/utilities/user_account_cleanup",
        name: "utilities_user_account_cleanup",
        component: () =>
            import(
          /* webpackChunkName: "group-utilities" */ "../views/utilities/user_account_cleanup/index.vue"
            ),
        meta: {
            allowedRoles: [Role.SUPER_ADMIN],
        },
    },

    // Home redirect logic
    {
        path: "/",
        name: "root",
        component: () => import( /* webpackChunkName: "group-app" */ '../views/home/index.vue'),
        meta: {
            allowedRoles: Role.All,
            title: "Home"
        },
    },

    // System
    {
        path: "*",
        name: "Catch all",
        redirect: () => {
            return "/";
        },
        meta: { allowedRoles: Role.All, redirect: true },
    },
];

const router = new VueRouter({
    mode: 'history',
    base: "/",
    routes
})

router.beforeEach((to, from, next) => {
    const user = volieStore.store.state.CurrentUser;

    // external redirect via query
    if (to.query.redirect_to) {
        window.location.assign(to.query.redirect_to as string);
        return next(false);
    }

    if (!user) {
        // redirect to login if no match and not authenticated
        window.location.replace("/auth/login");
        return next(false);
    }

    // restore saved page after session-invalidation logout/re-login
    const redirectAfterLogin = window.localStorage.getItem("volie:redirect_after_login");
    if (redirectAfterLogin) {
        window.localStorage.removeItem("volie:redirect_after_login");
        try {
            const savedUrl = new URL(redirectAfterLogin);
            if (savedUrl.origin === window.location.origin) {
                const target = savedUrl.pathname + savedUrl.search + savedUrl.hash;
                if (target !== to.fullPath) {
                    return next(target);
                }
            }
        } catch {
            // invalid URL, ignore
        }
    }

    // home page redirect logic
    if (to.path === "/") {
        switch (user.activated_role_id) {
            case Role.AGENT:
            case Role.ORGANIZATION_AGENT:
                return next("/communication_center");

            case Role.PURCHASED_LIST_ONLY:
                window.location.assign(
                    import.meta.env.VITE_APP_RAILS_VOLIE_BASE_URL +
                        "/purchased_lists"
                );
                return next(false);

            default:
                return next();
        }
    }

    // role authorization
    const authorized = to.matched.some(route =>
        route.meta.allowedRoles?.includes(user.activated_role_id)
    );

    if (!authorized) {
        console.error("role not authorized");
        return next("/");
    }


    if (from) {
        sessionStorage.setItem("route_from", from.path);
    }

    if (to) {
        sessionStorage.setItem("route_to", to.path);

        // record recent links in local storage
        if (to.name && to.meta.recordVisits === true) {
            // recent links
            const recentLinks = JSON.parse(localStorage.getItem('recentLinks') || '[]');

            // remove if already exists in array
            if (recentLinks.length > 0) {
                const idx = recentLinks.findIndex(route => route.name === to.name);
                if (idx !== -1) {
                    recentLinks.splice(idx, 1);
                }

                // only record 5 most recent
                if (recentLinks.length >= 5) {
                    recentLinks.pop();
                }
            }

            recentLinks.unshift({ name: to.name, title: to.meta.title || '' });
            recentLinks.splice(5);

            localStorage.setItem('recentLinks', JSON.stringify(recentLinks));
        }
    }

    DataBank?.onViewLoad();
    Action?.Clear();

    next();
});

export default router;
